import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { Link, useSearchParams } from 'react-router';
import {
  Button,
  Chips,
  Choice,
  DataTable,
  EmptyState,
  Icon,
  Input,
  Panel,
  Select,
  useErrorDialog,
  useToast,
  Pagination,
} from '@dayon/design-system';
import { formatCount } from '@dayon/money';
import { RESIDENCE_UNKNOWN, type PageMeta, type PlayerRow } from '@dayon/contracts';
import { api, ApiRequestError } from '../../lib/api.ts';
import { residenceLabel } from '../../lib/residence.tsx';
import { useSession } from '../../lib/session.tsx';
import { allBrandsLabel } from '../../lib/brand-words.ts';
import { ScreenHeader } from '../shell/ScreenHeader.tsx';
import { QuickLook } from './QuickLook.tsx';
import { playerColumns, type ColumnPrefs } from './columns.tsx';
import { SavedViewsDialog } from '../views/SavedViewsDialog.tsx';
import { joinPhrases } from './format.ts';
import { ExportDialog } from './dialogs/ExportDialog.tsx';
import { FiltersDialog, type FilterDraft } from './dialogs/FiltersDialog.tsx';
import { NoteDialog } from './dialogs/NoteDialog.tsx';
import { RestrictDialog } from './dialogs/RestrictDialog.tsx';
import { StatusDialog } from './dialogs/StatusDialog.tsx';
import { TierDialog } from './dialogs/TierDialog.tsx';
import {
  CHIPS,
  KYC_LABEL,
  SORTS,
  SORT_SENSE,
  STATUS_LABEL,
  STATUS_MOVES,
  TIER_LABEL,
  isChipKey,
  isSortDirection,
  isSortKey,
  type ChipKey,
  type SortDirection,
  type SortKey,
  type StatusMoveTarget,
} from './vocabulary.ts';

/**
 * Player management — the primary roster.
 *
 * READ THIS BEFORE ADDING AN ACTION. What this screen reads:
 *
 *   GET /api/players       list + count, filters q · brandId · status ·
 *                          kycStatus · tier · agentRef · residenceCountry,
 *                          sort LAST_SEEN | PLAYER | BALANCE | DEPOSITS | GGR |
 *                          REGISTERED, each in either direction.
 *                          `residenceCountry` takes one ISO 3166-1 alpha-2 code
 *                          or the sentinel UNKNOWN, which selects the players
 *                          who were never asked — a NULL column, not a country,
 *                          and never rendered as one
 *   GET /api/players/:id   one record + restrictions + notes + KYC submissions
 *   GET /api/transactions  the ledger, filterable by playerId — this is what
 *                          fills the quick look's "Last transactions"
 *
 * And the writes it sends, every one of them landing its change and its audit
 * row in a single transaction:
 *
 *   POST /api/players/restrictions   players.restrict — one control to the
 *                          whole selection, all of it or none of it
 *   POST /api/players/status         players.restrict — suspend, flag,
 *                          reinstate. Stricter than the restriction bulk: a
 *                          player whose current status does not permit the move
 *                          is named and NOTHING is written
 *   POST /api/players/tier           players.tier — a commercial decision, with
 *                          its own reason vocabulary
 *   POST /api/players/:id/notes      players.note — from the quick look and the
 *                          profile, one player at a time
 *   POST /api/players/export         audit.export — the roster through the
 *                          reports engine, stored as an artefact and downloaded
 *                          from it
 *
 * ONE CONTROL IS STILL INERT, and it is named on screen beside itself:
 *
 *   Message         — NO LONGER INERT, and this comment claimed it was for
 *                     four rounds. It said the audience vocabulary held only
 *                     whole-population rules and could not name individual
 *                     players. NAMED_PLAYERS is the fifth rule, the composer
 *                     resolves and counts it, and Message hands this screen's
 *                     selection to it in the address bar.
 *                     There is still no players.message capability, and there
 *                     does not need to be one: nothing is sent from here. The
 *                     selection is carried to the announcement composer, which
 *                     is gated on platform.manage and already holds the consent
 *                     rule, the address check and the per-recipient delivery
 *                     record. A promotional notice may not be addressed to a
 *                     hand-picked list at all — the API refuses the pair,
 *                     because choosing people by hand is exactly how a
 *                     self-excluded player is reached despite the opt-out.
 *   Adjust balance  — POST /api/adjustments EXISTS. It is not raised from here
 *                     because an adjustment is raised and then approved by a
 *                     second person, and both the form and that checker queue
 *                     are on Player funds. A one-click balance change from a
 *                     roster row would route around maker-checker, which is the
 *                     entire point of it.
 *
 * A toast reading "applied to 12 players" over a request that was never sent is
 * a lie an operator will act on — and on this screen the thing they would act on
 * is somebody's money.
 */

/* ── Page size and the bulk cap ─────────────────────────────────────────────
   "Page size is 8" and "Bulk actions cap at 500 rows per submission". The
   first is served by the API's perPage; the second is enforced on the wire as
   well — every bulk contract caps playerIds at 500 — and here too, so the
   operator meets the limit before the request rather than after. */
const PAGE_SIZE = 8;
const BULK_CAP = 500;

/* ── Chip badge state ──────────────────────────────────────────────────────
   Three states, not two. "The reads have not landed yet" and "the reads failed"
   are different facts about the world, and a single nullable counts object can
   only tell the operator one of them — which is how a first-load window came to
   print "the count reads failed" as a statement of fact. */
type CountsState =
  | { kind: 'loading' }
  | { kind: 'ready'; counts: Record<ChipKey, number> }
  | { kind: 'failed' };

/**
 * The columns popover.
 *
 * Anchored to the right edge of the HEADER ACTION GROUP rather than to the
 * Columns button itself, so the panel lines up with the edge of the content
 * column instead of hanging off a button somewhere in the middle of the row and
 * running past the right-hand side of the page on a narrow window.
 *
 * WHAT ACTUALLY KEEPS IT ON SCREEN, now that the header is a `<ScreenHeader>`:
 * that component lays its text block and its action slot out with
 * `justify-content: space-between`, so the action group is the right-most thing
 * in the header and its right edge IS the content column's right edge. `right:
 * 0` against it is therefore on screen at every width.
 *
 * This note used to credit `header-spacer` (margin-left: auto) for that, which
 * was true while the header was a hand-rolled `.row`. The class came out with
 * the hand-rolled header: inside the header's own action slot it has nothing to
 * push against and does nothing at all, and a dead class with a comment
 * explaining the layout is worse than no comment, because the next person to
 * move this panel would protect the wrong property.
 *
 * `position: relative` on the group is the part the panel genuinely depends on,
 * and it stays on the group for that reason rather than for any layout one.
 */
const popoverStyle: CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + var(--space-2))',
  right: 0,
  zIndex: 20,
  width: 'min(280px, calc(100vw - var(--space-6)))',
  boxShadow: 'var(--shadow-md)',
  animation: 'dropdown-pop 0.22s cubic-bezier(0.16, 1, 0.3, 1) forwards',
  transformOrigin: 'top right',
};

const subLine: CSSProperties = { fontSize: '11px' };

type DialogKey = 'filters' | 'views' | 'export' | 'restrict' | 'tier' | 'note' | StatusMoveTarget;

/**
 * EVERY FILTER THIS SCREEN UNDERSTANDS, read out of a query string.
 *
 * ── WHY ONE FUNCTION AND NOT TEN INITIALISERS ────────────────────────────
 *
 * These rules were written inline, once per `useState`, and were about to be
 * written a SECOND time: a saved view restores a stored query string, and
 * restoring it means the same ten decisions about what each parameter may
 * contain. Two copies of a parser is two answers to "is `?sort=NONSENSE` the
 * default or an error", and the screen would have given one answer to a pasted
 * URL and the other to a saved view of the same URL.
 *
 * ── AND THE VALIDATION IS THE POINT, NOT THE PARSING ─────────────────────
 *
 * A saved view is TEXT, stored months ago, by somebody who may have left. The
 * vocabulary it was saved against can change underneath it — a status chip
 * retired, a sort key renamed — so every closed-vocabulary value is checked and
 * falls back to the default rather than being passed through. An unrecognised
 * sort reaching the request is a 400 the operator did not cause and cannot read.
 *
 * The two that are NOT checked here are deliberate and were already so:
 * `residenceCountry` is parsed by the route against the same closed vocabulary
 * registration uses, so a hand-typed `?residenceCountry=XX` earns a named 400
 * rather than a silently ignored filter; and `kycStatus`, `tier` and `agentRef`
 * are free-form against their own endpoints for the same reason.
 */
function readFilters(params: URLSearchParams): {
  query: string;
  status: ChipKey;
  kycFilter: string;
  tierFilter: string;
  agentFilter: string;
  residenceFilter: string;
  testAccountFilter: string;
  sort: SortKey;
  direction: SortDirection;
  page: number;
} {
  const rawStatus = params.get('status');
  const rawSort = params.get('sort');
  const rawDirection = params.get('direction');
  const rawPage = Number(params.get('page'));
  return {
    query: params.get('q') ?? '',
    status: rawStatus && isChipKey(rawStatus) ? rawStatus : 'ALL',
    kycFilter: params.get('kycStatus') ?? '',
    tierFilter: params.get('tier') ?? '',
    agentFilter: params.get('agentRef') ?? '',
    residenceFilter: params.get('residenceCountry') ?? '',
    /* ANY unless the query says otherwise, so a shared link carries the choice
       and a bare /players shows the whole roster. */
    testAccountFilter: params.get('testAccount') ?? 'ANY',
    sort: rawSort && isSortKey(rawSort) ? rawSort : 'LAST_SEEN',
    direction: rawDirection && isSortDirection(rawDirection) ? rawDirection : 'DESC',
    page: Number.isInteger(rawPage) && rawPage > 0 ? rawPage : 1,
  };
}

/**
 * The toast after More filters is applied: every filter now on, or "Filters cleared." when none is.
 * The test-account filter is one of them — staging saw "Filters cleared." over a list it had
 * narrowed to one player — named in the words its own field offers.
 */
export function filtersToast(next: FilterDraft): string {
  const applied = [
    next.kyc ? `KYC ${KYC_LABEL[next.kyc] ?? next.kyc}` : null,
    next.tier ? `Tier ${TIER_LABEL[next.tier] ?? next.tier}` : null,
    next.agent ? `Agent ${next.agent}` : null,
    next.residence
      ? next.residence === RESIDENCE_UNKNOWN
        ? 'Residence never asked'
        : `Residence ${residenceLabel(next.residence)}`
      : null,
    next.testAccount === 'ONLY'
      ? 'Only test accounts'
      : next.testAccount === 'EXCLUDED'
        ? 'Test accounts hidden'
        : null,
  ].filter(Boolean);
  return applied.length > 0 ? `Filters applied · ${applied.join(' · ')}` : 'Filters cleared.';
}

export function Players() {
  const { brandId, brands, setBrandId, allowed, session } = useSession();
  const { showError } = useErrorDialog();
  const { toast } = useToast();
  /*
   * Every read on this screen is `GET /api/players`, which needs players.view. A role without it
   * — the PAGCOR Regulator — is refused below, and each effect that reads asks this first: the
   * refusal is a render, and an effect registered above it would still fetch on mount.
   */
  const canReadPlayers = allowed('players.view');

  /* Filters live in the URL: a filtered roster is the thing an operator pastes
     into a ticket, and a link can open it already filtered. Seeded lazily, then
     kept in step below. */
  const [searchParams, setSearchParams] = useSearchParams();
  const opening = readFilters(searchParams);
  const [query, setQuery] = useState(opening.query);
  const [status, setStatus] = useState<ChipKey>(opening.status);
  const [kycFilter, setKycFilter] = useState(opening.kycFilter);
  const [tierFilter, setTierFilter] = useState(opening.tierFilter);
  const [agentFilter, setAgentFilter] = useState(opening.agentFilter);
  const [residenceFilter, setResidenceFilter] = useState(opening.residenceFilter);
  const [testAccountFilter, setTestAccountFilter] = useState(opening.testAccountFilter);
  const [sort, setSort] = useState<SortKey>(opening.sort);
  const [direction, setDirection] = useState<SortDirection>(opening.direction);
  const [page, setPage] = useState(opening.page);

  const [rows, setRows] = useState<PlayerRow[]>([]);
  const [meta, setMeta] = useState<PageMeta | null>(null);
  const [loading, setLoading] = useState(true);
  /* "Nothing came back" and "nothing matched" are different answers and the
     empty state is not allowed to print the second one for the first. */
  const [listError, setListError] = useState(false);
  /* Set when the requested page turned out to be past the end of the result
     set, so the header, the table and the pager can say what happened. */
  const [pastEnd, setPastEnd] = useState<{ requested: number; landedOn: number } | null>(null);

  const [countsState, setCountsState] = useState<CountsState>({ kind: 'loading' });
  const [scopeTotal, setScopeTotal] = useState<number | null>(null);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [prefs, setPrefs] = useState<ColumnPrefs>({
    agent: true,
    kyc: true,
    deposits: true,
    lastSeen: true,
    /* Off by default — see the note on ColumnPrefs. Turned on automatically
       below when the operator filters BY residence, because a filtered roster
       that does not show the column it was filtered on is a table nobody can
       check. */
    residence: false,
  });
  const [columnsOpen, setColumnsOpen] = useState(false);
  const columnsRef = useRef<HTMLDivElement>(null);

  const [dialog, setDialog] = useState<DialogKey | null>(null);
  /* Bumped after a write so the roster, the chip counts and the scope total all
     re-read. A restriction moves an ACTIVE player to RESTRICTED, which moves
     two chip badges and the row itself — patching the row in place would leave
     the badges stating a roster that no longer exists. */
  const [writeCount, setWriteCount] = useState(0);

  const [quickLook, setQuickLook] = useState<PlayerRow | null>(null);
  /*
   * The player a note is being written on, held SEPARATELY from the quick look.
   *
   * The note dialog opens over the drawer, and Escape reaches both — the drawer
   * closes on it and this dialog does not, deliberately, because a decision the
   * operator must answer explicitly should not vanish on a stray key. Deriving
   * the target from `quickLook` meant the drawer closing unmounted the dialog
   * and threw away a half-typed note, which is the one thing this control must
   * never do.
   */
  const [noteTarget, setNoteTarget] = useState<{ id: string; name: string } | null>(null);

  /**
   * Put a SAVED VIEW back on the screen.
   *
   * The exact inverse of the URL-sync effect below, and it goes through
   * `readFilters` so the two cannot disagree: what a saved view restores and
   * what a pasted link restores are the same ten decisions, made once.
   *
   * THE PAGE GOES BACK TO 1, deliberately, however deep the view was saved from.
   * Page 12 of one filter set is not page 12 of another, and a view that opened
   * on an empty page would read as "no players match" to somebody who had just
   * chosen the view expecting rows.
   *
   * The address bar is written LAST, so a restored view is still a link the
   * operator can copy into a ticket — which is most of why the filters live in
   * the URL at all.
   */
  const applyQuery = (q: string) => {
    const next = new URLSearchParams(q);
    const f = readFilters(next);
    setQuery(f.query);
    setStatus(f.status);
    setKycFilter(f.kycFilter);
    setTierFilter(f.tierFilter);
    setAgentFilter(f.agentFilter);
    setResidenceFilter(f.residenceFilter);
    setTestAccountFilter(f.testAccountFilter);
    setSort(f.sort);
    setDirection(f.direction);
    setPage(1);
    setSearchParams(next);
  };

  /* ── URL sync ────────────────────────────────────────────────────────────
     Guarded by a string compare so the write that follows a read cannot loop. */
  useEffect(() => {
    const next = new URLSearchParams();
    if (query.trim()) next.set('q', query.trim());
    if (status !== 'ALL') next.set('status', status);
    if (kycFilter) next.set('kycStatus', kycFilter);
    if (tierFilter) next.set('tier', tierFilter);
    if (agentFilter) next.set('agentRef', agentFilter);
    if (residenceFilter) next.set('residenceCountry', residenceFilter);
    if (testAccountFilter !== 'ANY') next.set('testAccount', testAccountFilter);
    if (sort !== 'LAST_SEEN') next.set('sort', sort);
    if (direction !== 'DESC') next.set('direction', direction);
    if (page > 1) next.set('page', String(page));
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [
    query,
    status,
    kycFilter,
    tierFilter,
    agentFilter,
    residenceFilter,
    testAccountFilter,
    sort,
    direction,
    page,
    searchParams,
    setSearchParams,
  ]);

  /* ── The list ────────────────────────────────────────────────────────────
     Debounced: a request per keystroke against the roster is wasteful and makes
     the body flicker between stale responses. */
  useEffect(() => {
    if (!canReadPlayers) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      setLoading(true);
      const params = new URLSearchParams({
        page: String(page),
        perPage: String(PAGE_SIZE),
        sort,
        direction,
      });
      if (query.trim()) params.set('q', query.trim());
      if (status !== 'ALL') params.set('status', status);
      if (kycFilter) params.set('kycStatus', kycFilter);
      if (tierFilter) params.set('tier', tierFilter);
      if (agentFilter) params.set('agentRef', agentFilter);
      if (residenceFilter) params.set('residenceCountry', residenceFilter);
      if (testAccountFilter !== 'ANY') params.set('testAccount', testAccountFilter);
      if (brandId) params.set('brandId', brandId);

      api
        .get<{ items: PlayerRow[]; meta: PageMeta }>(`/api/players?${params}`)
        .then((d) => {
          if (cancelled) return;
          setRows(d.items);
          setMeta(d.meta);
          setListError(false);
        })
        .catch((error: unknown) => {
          if (cancelled) return;
          /* The rows and the meta go with the failure. Leaving the previous
             response on screen under the filter chips the operator has just
             changed is a table captioned by a query it did not answer. */
          setRows([]);
          setMeta(null);
          setListError(true);
          showError({
            title: 'Could not load players',
            message:
              error instanceof ApiRequestError
                ? error.message
                : 'The roster could not be fetched. Try again shortly.',
            ...(error instanceof ApiRequestError ? { code: error.code } : {}),
          });
        })
        .finally(() => !cancelled && setLoading(false));
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    canReadPlayers,
    query,
    status,
    kycFilter,
    tierFilter,
    agentFilter,
    residenceFilter,
    testAccountFilter,
    sort,
    direction,
    page,
    brandId,
    writeCount,
    showError,
  ]);

  /* ── Chip counts ─────────────────────────────────────────────────────────
     The design shows a count on every chip and there is no counts endpoint, so
     each one is a real HEAD-sized read: the same filtered query with perPage=1,
     taking meta.total. Five small round trips, not five guesses.

     The counts carry the brand scope, the search and the More-filters selection
     — everything except the chip itself — so a chip saying 7 always produces
     exactly 7 rows. The badges are also DROPPED the instant a filter changes,
     before the debounce, and only repainted when the new reads land. */
  useEffect(() => {
    if (!canReadPlayers) return;
    let cancelled = false;
    setCountsState({ kind: 'loading' });
    const timer = setTimeout(() => {
      const base = new URLSearchParams({ perPage: '1' });
      if (query.trim()) base.set('q', query.trim());
      if (kycFilter) base.set('kycStatus', kycFilter);
      if (tierFilter) base.set('tier', tierFilter);
      if (agentFilter) base.set('agentRef', agentFilter);
      if (residenceFilter) base.set('residenceCountry', residenceFilter);
      if (testAccountFilter !== 'ANY') base.set('testAccount', testAccountFilter);
      if (brandId) base.set('brandId', brandId);

      Promise.all(
        CHIPS.map((chip) => {
          const params = new URLSearchParams(base);
          if (chip.key !== 'ALL') params.set('status', chip.key);
          return api
            .get<{ meta: PageMeta }>(`/api/players?${params}`)
            .then((d) => [chip.key, d.meta.total] as const);
        }),
      )
        .then((pairs) => {
          if (cancelled) return;
          setCountsState({
            kind: 'ready',
            counts: Object.fromEntries(pairs) as Record<ChipKey, number>,
          });
        })
        // A failed count is a missing badge, not a dialog over the whole screen.
        .catch(() => !cancelled && setCountsState({ kind: 'failed' }));
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    canReadPlayers,
    query,
    kycFilter,
    tierFilter,
    agentFilter,
    residenceFilter,
    testAccountFilter,
    brandId,
    writeCount,
  ]);

  /* The denominator in "7 of 214 players" — the brand scope with nothing else
     applied. Refetched only when the scope changes. */
  useEffect(() => {
    if (!canReadPlayers) return;
    let cancelled = false;
    const params = new URLSearchParams({ perPage: '1' });
    if (brandId) params.set('brandId', brandId);
    api
      .get<{ meta: PageMeta }>(`/api/players?${params}`)
      .then((d) => !cancelled && setScopeTotal(d.meta.total))
      .catch(() => !cancelled && setScopeTotal(null));
    return () => {
      cancelled = true;
    };
  }, [canReadPlayers, brandId, writeCount]);

  /* A scope change re-cuts the roster; a selection carried across it would arm
     rows the operator can no longer see. Adjusted during render rather than in
     an effect, so the table never paints one frame of the old selection over
     the new scope. */
  const [scopeAtLastReset, setScopeAtLastReset] = useState(brandId);
  if (scopeAtLastReset !== brandId) {
    setScopeAtLastReset(brandId);
    setSelected(new Set());
    setPage(1);
  }

  /* ── Page clamp ──────────────────────────────────────────────────────────
     meta.total is the true size of the result set even on a page past the end —
     the route re-probes when the window comes back empty — so an out-of-range
     ?page= is recoverable rather than a dead end. */
  useEffect(() => {
    if (!meta || meta.totalPages < 1) return;
    const landedOn = meta.totalPages;
    if (meta.page > landedOn) {
      setPastEnd((current) =>
        current && current.requested === meta.page && current.landedOn === landedOn
          ? current
          : { requested: meta.page, landedOn },
      );
      setPage(landedOn);
    } else {
      setPastEnd((current) => (current && meta.page !== current.landedOn ? null : current));
    }
  }, [meta]);

  /* The columns popover is inline, so it closes on an outside press and on
     Escape — otherwise it survives every click on the table beneath it. */
  useEffect(() => {
    if (!columnsOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!columnsRef.current?.contains(e.target as Node)) setColumnsOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setColumnsOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [columnsOpen]);

  /* ── Derived ─────────────────────────────────────────────────────────── */

  const brandLabel = brandId
    ? (brands.find((b) => b.id === brandId)?.name ?? 'Selected brand')
    : allBrandsLabel(brands);

  const atCap = selected.size >= BULK_CAP;
  const pageIds = rows.map((r) => r.id);
  const allOnPageSelected = rows.length > 0 && pageIds.every((id) => selected.has(id));
  const selectedIds = useMemo(() => [...selected], [selected]);

  /* The test-account filter narrows the read like the others (it lives in More filters), so an
     empty list under it is not "a real zero … with no search or filter narrowing it". */
  const moreFilters =
    Boolean(kycFilter || tierFilter || agentFilter || residenceFilter) ||
    testAccountFilter !== 'ANY';
  const narrowed = query.trim().length > 0 || status !== 'ALL' || moreFilters;

  /* Exactly what the empty state's clear button will throw away, named. */
  const narrowings = [
    query.trim() ? 'search' : null,
    status !== 'ALL' ? 'status filter' : null,
    moreFilters ? 'More filters' : null,
  ].filter((v): v is string => v !== null);
  const clearNarrowingLabel = `Clear ${joinPhrases(narrowings)}`;

  /* Named individually for the export notice, which has to say WHICH filters it
     is not carrying rather than that some exist. */
  const exportUncarried = [
    query.trim() ? `search “${query.trim()}”` : null,
    status !== 'ALL' ? `status ${STATUS_LABEL[status] ?? status}` : null,
    kycFilter ? `KYC ${KYC_LABEL[kycFilter] ?? kycFilter}` : null,
    tierFilter ? `tier ${TIER_LABEL[tierFilter] ?? tierFilter}` : null,
    agentFilter ? `agent ${agentFilter}` : null,
    residenceFilter
      ? `residence ${residenceFilter === RESIDENCE_UNKNOWN ? 'never asked' : residenceLabel(residenceFilter)}`
      : null,
  ].filter((v): v is string => v !== null);

  const lastPage = meta && meta.totalPages > 0 ? meta.totalPages : 1;
  const pageIsPastEnd = meta !== null && meta.totalPages > 0 && meta.page > meta.totalPages;

  const countLabel = meta
    ? narrowed && scopeTotal !== null
      ? `${formatCount(meta.total)} of ${formatCount(scopeTotal)} players`
      : `${formatCount(meta.total)} player${meta.total === 1 ? '' : 's'}`
    : listError
      ? // Not "Loading…": the read is over and it failed.
        'Player count unavailable — the roster read failed'
      : 'Loading…';

  /* `count` is omitted, never zeroed, while the reads are in flight — Chips
     hides the badge for undefined, and a 0 badge on a chip that has rows is
     exactly the kind of authoritative-looking wrong number this screen must
     never print. */
  const chipOptions = CHIPS.map((c) =>
    countsState.kind === 'ready'
      ? { key: c.key, label: c.label, count: countsState.counts[c.key] }
      : { key: c.key, label: c.label },
  );

  const columns = useMemo(
    () =>
      playerColumns({
        prefs,
        selected,
        atCap,
        onToggle: (id) =>
          setSelected((current) => {
            const next = new Set(current);
            if (next.has(id)) next.delete(id);
            else if (next.size < BULK_CAP) next.add(id);
            return next;
          }),
        onOpen: setQuickLook,
        sort,
        direction,
      }),
    [prefs, selected, atCap, sort, direction],
  );

  const canRestrict = allowed('players.restrict');
  const canAdjust = allowed('funds.adjust');
  const canExport = allowed('audit.export');
  const canSetTier = allowed('players.tier');
  const canNote = allowed('players.note');
  /**
   * The composer Message hands the selection TO, which is a different module
   * with a different key.
   *
   * Every other control in the bulk bar is gated on what it needs. Message was
   * not gated on anything at all, so a Support Agent — who may select players,
   * and may not open announcements — was offered the button, carried to
   * `/announcements`, and met "This module needs the access.manage capability.
   * Your role does not hold it." The roster had sent them somewhere it knew
   * they could not go.
   */
  const canAnnounce = allowed('access.manage');
  const role = session?.role ?? 'your role';

  // Below every hook, so the hooks run in the same order for every reader; the
  // reads above are what `canReadPlayers` stops.
  if (!canReadPlayers) {
    return (
      <EmptyState icon="lock-key" title="Player management">
        This module needs the players.view capability. Your role does not hold it.
      </EmptyState>
    );
  }

  const reload = () => {
    setSelected(new Set());
    setWriteCount((n) => n + 1);
  };

  /* The design's clear-the-query control resets the status chip and the page
     along with the text. */
  const resetSearch = () => {
    setQuery('');
    setStatus('ALL');
    setPage(1);
  };

  const clearNarrowing = () => {
    setQuery('');
    setStatus('ALL');
    setKycFilter('');
    setTierFilter('');
    setAgentFilter('');
    setResidenceFilter('');
    setTestAccountFilter('ANY');
    setPage(1);
  };

  const applyFilters = (next: FilterDraft) => {
    setKycFilter(next.kyc);
    setTierFilter(next.tier);
    setAgentFilter(next.agent);
    setResidenceFilter(next.residence);
    setTestAccountFilter(next.testAccount);
    /* Filtering BY residence turns the residence column ON. A roster narrowed
       to one country with that column hidden shows eight rows the operator has
       to take on trust; the column is the only thing on screen that proves the
       filter did what it says. It is not turned back off on clearing — an
       operator who has seen the column and wants it gone has the picker. */
    if (next.residence) setPrefs((prev) => ({ ...prev, residence: true }));
    setPage(1);
    setDialog(null);
    toast(filtersToast(next));
  };

  /* What an empty table is allowed to say. These are four different facts —
     past the end of a non-empty result set, narrowed to nothing, a genuine zero
     the route reported, and two reads that crossed. The fifth, a failed read,
     does not reach here: it renders its own empty state.

     None of the four names the endpoint any more. An operator reading
     "/api/players returned no rows" cannot open that address and cannot quote
     it to anybody who will act on it, and it tells them nothing that "the
     roster was read" does not tell them better — what they need from an empty
     table is whether the read answered or failed, and every one of these keeps
     saying that. The route behind all four is GET /api/players, recorded with
     its filters and its sorts at the top of this file; it belongs there and not
     in the sentence. "The roster read" is the same phrase `countLabel` uses a
     hundred lines up, so the caption and the empty state name one thing. */
  let emptyBody: string;
  if (pageIsPastEnd && meta) {
    emptyBody =
      `Page ${formatCount(meta.page)} is past the end of this result set — the roster was read and ` +
      `reports ${formatCount(meta.total)} matching player${meta.total === 1 ? '' : 's'}, ` +
      `ending on page ${formatCount(lastPage)}. This is not an empty roster.`;
  } else if (narrowed) {
    emptyBody =
      `Try a player ID or a phone number, or drop a narrowing — the button below clears ` +
      `${joinPhrases(narrowings)}. Search covers player ID, display name, phone with or without ` +
      `+63, email and agent code.`;
  } else if (meta && meta.total === 0) {
    emptyBody =
      `A real zero — the roster was read and reports no players at all for ${brandLabel}, ` +
      `with no search or filter narrowing it.`;
  } else if (meta) {
    emptyBody =
      `No rows came back for page ${formatCount(meta.page)}, though the roster read reports ` +
      `${formatCount(meta.total)} in this view. The roster may have changed between the two ` +
      `reads — reload to re-read it.`;
  } else {
    emptyBody = 'Nothing has been read for this scope yet.';
  }

  return (
    <>
      {/*
        WHAT THIS SCREEN IS FOR, SAID ON THE SCREEN.

        The reason this module exists has been written at the top of this file
        for a long time, in detail, and read only by whoever was editing the
        file. An operator arriving here met a name, a count and a table of
        eight rows, and had to work out from the rows that this is the place you
        come to find one account and do something to it — which matters more
        here than on most screens, because nearly every other module deep-links
        INTO this one. The dashboard sends people here with a KYC filter already
        applied; Agents sends them here scoped to one agent's players. Somebody
        who arrives that way has not chosen the screen and does not necessarily
        know what it is.

        It could not be folded into the notice below instead. That notice is
        about the ACTIONS — which of the six write, which one only hands the
        selection somewhere else, and why adjusting a balance deliberately is
        not offered here — and it is already long. A sentence about the screen
        stacked on top of a sentence about the controls reads as one wall and
        gets skipped as one wall. The header is the one place a line about the
        SCREEN can sit without competing with a statement about the rows.
      */}
      <ScreenHeader
        title="Player management"
        purpose="The main list of every player — where support and risk staff find an account and act on it, one player or a whole selection at once."
        /*
          The caption moves across unchanged, both halves of it.

          `countLabel` is tri-state and the three states are not
          interchangeable. 'Loading…' means the answer has not arrived yet;
          'Player count unavailable — the roster read failed' means the request
          failed, so there is no number to show and none was ever made; and a
          figure means the route answered. Collapsing that middle state into a
          zero, or into 'Loading…' that never finishes, is the exact mistake the
          note beside `countLabel` above exists to prevent — "this brand has no
          players" is something an operator would act on and "nobody managed to
          ask" is not. The table's empty state, the chip badges and this line all
          lean on the same two flags for the same reason, and they have to agree.

          The brand scope stays appended to whichever state won, because it is
          true of all three: a failed read still failed inside a scope, and a
          reader who does not know whether they are looking at one brand or at
          all of them cannot judge the number either way.
        */
        meta={
          <>
            {countLabel} · {brandLabel}
          </>
        }
        /*
          The action group moves across whole, ref and all.

          It is one positioned element rather than three loose buttons on
          purpose: the Columns panel hangs off its right edge, and the
          outside-press handler that closes that panel tests `columnsRef`, so
          the buttons and the panel have to stay inside the same box or a click
          on Columns would register as a click outside Columns and shut the
          panel in the same gesture that opened it. See the long note on
          `popoverStyle` for which property the anchoring actually depends on.
        */
        actions={
          <div ref={columnsRef} className="row" style={{ position: 'relative' }}>
            <Button
              onClick={() => {
                setColumnsOpen(false);
                setDialog('filters');
              }}
            >
              <Icon name="funnel" />
              More filters
            </Button>

            {/* Saved views sits beside More filters because that is the control
                it finishes: one chooses a filter set, the other keeps it. The
                console told operators for months that views could not be stored
                — the table and the routes were both live — and `/transactions`
                was the only screen that ever mounted this. */}
            <Button
              onClick={() => {
                setColumnsOpen(false);
                setDialog('views');
              }}
            >
              <Icon name="bookmark-simple" />
              Saved views
            </Button>

            <Button
              aria-label="Choose visible columns"
              aria-expanded={columnsOpen}
              onClick={() => setColumnsOpen((open) => !open)}
            >
              <Icon name="columns" />
              Columns
            </Button>

            {/* Hidden, not disabled, for a role that does not hold the
                capability — a greyed control still advertises a power the person
                does not have. The bulk bar below takes the opposite line for
                Restrict and Set tier, deliberately: those sit beside a live
                selection, and an operator who has selected twelve players needs
                to be told WHY the action they reached for is not theirs. */}
            {canExport && (
              <Button
                variant="primary"
                onClick={() => {
                  setColumnsOpen(false);
                  setDialog('export');
                }}
              >
                <Icon name="download-simple" />
                Export
              </Button>
            )}

            {columnsOpen && (
              <div
                className="panel stack"
                role="group"
                aria-label="Optional columns"
                style={popoverStyle}
              >
                <div className="panel-title">Optional columns</div>
                <Choice
                  checked={prefs.agent}
                  onChange={(e) => setPrefs((p) => ({ ...p, agent: e.target.checked }))}
                  label="Agent"
                />
                <Choice
                  checked={prefs.kyc}
                  onChange={(e) => setPrefs((p) => ({ ...p, kyc: e.target.checked }))}
                  label="KYC"
                />
                <Choice
                  checked={prefs.deposits}
                  onChange={(e) => setPrefs((p) => ({ ...p, deposits: e.target.checked }))}
                  label="Lifetime deposits"
                />
                <Choice
                  checked={prefs.lastSeen}
                  onChange={(e) => setPrefs((p) => ({ ...p, lastSeen: e.target.checked }))}
                  label="Last seen"
                />
                <Choice
                  checked={prefs.residence}
                  onChange={(e) => setPrefs((p) => ({ ...p, residence: e.target.checked }))}
                  label="Residence"
                />
                <div className="field-hint">
                  {/* Component state only. A per-admin store now exists (/api/views/columns, used
                      by Transactions' column picker), so "no per-admin column preference table"
                      was no longer true; the roster simply does not use it yet. */}
                  Applies for as long as this table is open — leaving the roster and coming back
                  resets it, well before the session ends. This screen does not save the choice,
                  so nothing here is remembered.
                </div>
              </div>
            )}
          </div>
        }
      />

      <div className="notice notice-quiet">
        <Icon name="lock-key" />
        <div>
          <strong>What this screen changes.</strong> You can apply a control, suspend, flag or
          reinstate an account, move a tier, and append an internal note — each acting on your whole
          selection at once, all of it or none of it, and each writing its{' '}
          audit row with the change or not at all.{' '}
          <strong>Export</strong> runs the roster through the reports engine, with the same
          read-only access, row cap and CSV guard as every other extract.{' '}
          <strong>Message</strong> sends nothing from this screen: it carries your selection to
          the announcement composer, which is where writing to many people already works — and
          where the marketing-consent rule, the address check and the per-recipient delivery
          record already live. Nothing is written until you compose the notice and send it there,
          and a notice addressed to a list you picked by hand may only be an operational one:
          choosing people individually is precisely how a self-excluded player would be reached
          by a promotion despite the opt-out, so that pair is refused.{' '}
          <strong>Adjust balance</strong> is disabled for a different reason: adjusting a balance
          is deliberately two steps, raised by one person and approved by another on{' '}
          <strong>Player funds</strong>, and a one-click change from a roster row would route
          around exactly that. Search, the status chips, KYC / Tier / Agent /
          Country-of-residence filtering, sorting in both directions and paging all work here for
          real.
        </div>
      </div>

      <div className="row">
        <div className="search-field" style={{ flex: '1 1 260px', maxWidth: '420px' }}>
          <Icon name="magnifying-glass" />
          <Input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(1);
            }}
            placeholder="Name, player ID, phone, agent"
            aria-label="Search players by name, player ID, phone, email or agent code"
          />
          {query && (
            <Button
              iconOnly
              variant="ghost"
              aria-label={
                status === 'ALL'
                  ? 'Clear search'
                  : `Clear search and reset the status filter from ${STATUS_LABEL[status] ?? status} to All`
              }
              onClick={resetSearch}
            >
              <Icon name="x" />
            </Button>
          )}
        </div>

        {/* Sort is a control, not a clickable header: the shared DataTable
            renders plain string headers, and a table you cannot sort from the
            keyboard is worse than one sorted from a Select. All six keys are
            served by fixed SQL, so none of them is disabled any more. */}
        <Select
          aria-label="Sort players by"
          value={sort}
          onChange={(e) => {
            if (isSortKey(e.target.value)) setSort(e.target.value);
            setPage(1);
          }}
          style={{ maxWidth: '260px' }}
        >
          {SORTS.map((s) => (
            <option key={s.key} value={s.key}>
              Sort: {s.label}
            </option>
          ))}
        </Select>

        <Button
          iconOnly
          aria-label={
            direction === 'DESC'
              ? `Sort ascending — ${SORT_SENSE[sort].ASC}`
              : `Sort descending — ${SORT_SENSE[sort].DESC}`
          }
          aria-describedby="players-sort-gap"
          onClick={() => {
            setDirection((d) => (d === 'DESC' ? 'ASC' : 'DESC'));
            setPage(1);
          }}
        >
          <Icon name={direction === 'DESC' ? 'sort-descending' : 'sort-ascending'} />
        </Button>
      </div>

      <div className="field-hint" id="players-sort-gap">
        Ordered by <strong>{SORTS.find((s) => s.key === sort)?.label}</strong> ·{' '}
        {SORT_SENSE[sort][direction]}. The order is applied across the whole roster, not just to
        the rows on this page, so it holds from page to page — and every
        one of the twelve orders ends on something no two players share, so no row can appear on
        two pages or vanish between them.{' '}
        {sort === 'DEPOSITS' && !prefs.deposits
          ? 'The Lifetime deposits column is hidden right now, so the rows are ordered by a figure this table is not showing at all — turn it on under Columns to see it.'
          : null}
        {sort === 'GGR'
          ? 'Lifetime GGR is not one of the columns here — it is in the quick look, where every row shows its own.'
          : null}
        {sort === 'REGISTERED'
          ? 'Registered is not one of the columns here either; the registration date is on the quick look and the profile.'
          : null}
      </div>

      <Chips
        label="Filter players by status"
        options={chipOptions}
        active={status}
        onChange={(key) => {
          setStatus(key);
          setPage(1);
        }}
      />

      <div className="row">
        <Choice
          checked={allOnPageSelected}
          // The design puts this in the header cell. DataTable takes a string
          // header, so it sits above the table — same behaviour, and it can say
          // out loud how many rows it arms, which a bare header box cannot.
          onChange={(e) =>
            setSelected((current) => {
              const next = new Set(current);
              if (e.target.checked) {
                for (const id of pageIds) {
                  if (next.size < BULK_CAP) next.add(id);
                }
              } else {
                for (const id of pageIds) next.delete(id);
              }
              return next;
            })
          }
          disabled={rows.length === 0}
          label={`Select all ${rows.length} on this page`}
        />
        {countsState.kind === 'loading' && (
          <span className="text-subtle" style={subLine}>
            Counting each status… the badges appear when all five reads land.
          </span>
        )}
        {countsState.kind === 'failed' && (
          <span className="text-subtle" style={subLine}>
            Chip counts unavailable — the count reads failed.{' '}
            {listError
              ? 'The list read failed too, so nothing on this screen is a current count.'
              : 'The table itself is current.'}
          </span>
        )}
      </div>

      {selected.size > 0 && (
        <Panel className="stack">
          <div className="row">
            <strong>
              {formatCount(selected.size)} selected
              {atCap ? ` · at the ${formatCount(BULK_CAP)}-row cap` : ''}
            </strong>
            <Button
              disabled={!canSetTier}
              aria-describedby="players-bulk-gap"
              onClick={() => setDialog('tier')}
            >
              Set tier
            </Button>
            {STATUS_MOVES.map((move) => (
              <Button
                key={move.status}
                disabled={!canRestrict}
                aria-describedby="players-bulk-gap"
                onClick={() => setDialog(move.status)}
              >
                {move.label}
              </Button>
            ))}
            {/*
              * MESSAGE HANDS THE SELECTION TO THE ANNOUNCEMENT COMPOSER, which
              * is where writing to many people already works — and where the
              * consent rule, the suppression list, the address check and the
              * per-recipient delivery record already live.
              *
              * A second "email these players" path would have had none of them.
              */}
            {canAnnounce ? (
              <Link
                className="btn btn-secondary"
                to={`/announcements?audience=NAMED_PLAYERS&players=${[...selected].join(',')}`}
              >
                Message {formatCount(selected.size)}
              </Link>
            ) : (
              /*
               * DISABLED AND EXPLAINED, matching every other control in this
               * bar rather than disappearing. The selection is still valid and
               * the other five actions still work on it; what is missing is the
               * key to the module this one hands off to, and saying which one
               * is the difference between "you cannot" and "you cannot, and
               * here is what to ask for".
               */
              <Button variant="secondary" disabled aria-describedby="players-bulk-gap">
                Message {formatCount(selected.size)}
              </Button>
            )}
            <Button
              variant="primary"
              disabled={!canRestrict}
              aria-describedby="players-bulk-gap"
              onClick={() => setDialog('restrict')}
            >
              Restrict
            </Button>
            <Button variant="ghost" onClick={() => setSelected(new Set())}>
              Clear
            </Button>
          </div>

          <div className="notice notice-quiet" id="players-bulk-gap">
            <Icon name="lock-key" />
            <div>
              <strong>Five of the six write.</strong> <strong>Restrict</strong> applies one control
              to the whole selection; <strong>Suspend</strong>, <strong>Flag</strong> and{' '}
              <strong>Reinstate</strong> move a player’s status;{' '}
              <strong>Set tier</strong> moves a player’s tier.
              Each writes its own{' '}
              status-history row and one{' '}
              audit entry per player, in a single
              transaction — a submission is every player or none of them. A selection carrying
              somebody who cannot make the move is refused by name with nothing written, rather than
              half-applied.{' '}
              <strong>Message hands the selection to the announcement composer</strong> rather
              than sending anything from here.{' '}
              {!canAnnounce && (
                <>
                  It is unavailable to you: the composer needs{' '}
                  <span className="signin-perm">access.manage</span>, which {role} does not hold.
                  The other actions here are unaffected.{' '}
                </>
              )} Writing to many people already works there, and so
              do the rules that make it safe: the address check, the suppression list, the
              per-recipient delivery record, and the consent rule that decides who may be marketed
              to. A second way to email a player would have had none of them.
              <br />
              <strong>A chosen list can carry an operational notice and never a promotion.</strong>{' '}
              An outage notice to the fourteen people it affected is a real thing to send. A
              hand-picked PROMOTIONAL list is a targeted marketing send, and this platform addresses
              marketing by recorded consent rather than by selection — allowing it would let
              somebody route round that by choosing the people one at a time. The composer refuses
              the combination and so does the platform behind it.{' '}
              <strong>A note is not a bulk action</strong>: the same sentence pasted onto twelve
              accounts is twelve records describing one of them, so notes are written per player
              from the quick look and the profile.
              {!canRestrict && (
                <>
                  {' '}
                  Restrict, Suspend, Flag and Reinstate are outside your role: they need{' '}
                  <span className="tabular">players.restrict</span> and you are {role}.
                </>
              )}
              {!canSetTier && (
                <>
                  {' '}
                  Set tier needs <span className="tabular">players.tier</span>, which {role} does
                  not hold — a tier is a commercial decision rather than a control.
                </>
              )}{' '}
              The {formatCount(BULK_CAP)}-row cap is enforced on the wire as well as in the page.
            </div>
          </div>
        </Panel>
      )}

      {pastEnd && meta && (
        <div className="notice notice-quiet">
          <Icon name="info" />
          <div>
            Page {formatCount(pastEnd.requested)} is past the end of this view.{' '}
            There {meta.total === 1 ? 'is' : 'are'} {formatCount(meta.total)} player
            {meta.total === 1 ? '' : 's'} here, which is {formatCount(lastPage)} page
            {lastPage === 1 ? '' : 's'} at {formatCount(PAGE_SIZE)} a page, so this is page{' '}
            {formatCount(pastEnd.landedOn)} — the last one. The address bar was updated to match.
          </div>
        </div>
      )}

      <Panel>
        {loading && rows.length === 0 ? (
          <div className="skeleton" style={{ height: '240px' }}>
            &nbsp;
          </div>
        ) : (
          <DataTable
            caption="Players, with brand, agent, tier, KYC state, status, cash balance and last seen"
            columns={columns}
            rows={rows}
            rowKey={(p) => p.id}
            empty={
              listError ? (
                /* The read failed. Nothing here is a statement about the
                   roster — the roster was never seen. */
                <EmptyState icon="warning-circle" title="The roster could not be read">
                  This table is empty because the roster could not be fetched, not because no
                  player matched. The message that opened carries the reason; change a filter or
                  reload to try again.
                </EmptyState>
              ) : (
                <EmptyState
                  icon={pageIsPastEnd ? 'file-dashed' : 'users-three'}
                  title={
                    pageIsPastEnd
                      ? 'Nothing on this page'
                      : query.trim()
                        ? `No players match “${query.trim()}”`
                        : 'No players in scope'
                  }
                  action={
                    pageIsPastEnd ? (
                      <Button onClick={() => setPage(lastPage)}>
                        Go to page {formatCount(lastPage)}
                      </Button>
                    ) : narrowed ? (
                      <Button onClick={clearNarrowing}>{clearNarrowingLabel}</Button>
                    ) : brandId ? (
                      <Button onClick={() => setBrandId(null)}>Show all brands</Button>
                    ) : null
                  }
                >
                  {emptyBody}
                </EmptyState>
              )
            }
          />
        )}
        {meta && (
          <Pagination
            numbered
            page={meta.page}
            perPage={meta.perPage}
            total={meta.total}
            onPage={setPage}
          />
        )}
      </Panel>

      {quickLook && (
        <QuickLook
          row={quickLook}
          onClose={() => setQuickLook(null)}
          canAdjust={canAdjust}
          canNote={canNote}
          canViewLedger={allowed('transactions.view')}
          role={session?.role ?? 'unknown'}
          onAddNote={() => {
            setNoteTarget({ id: quickLook.id, name: quickLook.name });
            setDialog('note');
          }}
        />
      )}

      {/* ── Saved views ──────────────────────────────────────────────────
          `currentQuery` is the live address bar rather than a rebuilt string:
          the URL-sync effect above is the one writer of that query, so saving
          what is in the bar cannot drift from what the screen is showing. */}
      <SavedViewsDialog
        open={dialog === 'views'}
        screen="PLAYERS"
        screenLabel="Player management"
        currentQuery={searchParams.toString()}
        onClose={() => setDialog(null)}
        onApply={applyQuery}
      />

      <FiltersDialog
        open={dialog === 'filters'}
        onClose={() => setDialog(null)}
        current={{
          kyc: kycFilter,
          tier: tierFilter,
          agent: agentFilter,
          residence: residenceFilter,
          testAccount: testAccountFilter,
        }}
        onApply={applyFilters}
      />

      <ExportDialog
        open={dialog === 'export'}
        onClose={() => setDialog(null)}
        brandId={brandId}
        brandLabel={brandLabel}
        filtersApplied={exportUncarried}
      />

      <RestrictDialog
        open={dialog === 'restrict'}
        onClose={() => setDialog(null)}
        playerIds={selectedIds}
        onDone={reload}
      />

      <TierDialog
        open={dialog === 'tier'}
        onClose={() => setDialog(null)}
        playerIds={selectedIds}
        onDone={reload}
      />

      {/* One dialog per target, so each carries its own form state: a note
          typed for a suspension must not survive into a reinstatement, where it
          would be filed under a different vocabulary of reasons. */}
      {STATUS_MOVES.map((move) => (
        <StatusDialog
          key={move.status}
          open={dialog === move.status}
          onClose={() => setDialog(null)}
          target={move.status}
          playerIds={selectedIds}
          onDone={reload}
        />
      ))}

      {/* Notes are per player, so this is opened from the quick look rather
          than from the bulk bar. It lives here — outside the drawer, and keyed
          on its own target rather than on the open drawer — because <Dialog>
          unmounts its children, and a half-typed note must survive both a
          mis-clicked Cancel and the drawer closing underneath it. */}
      {noteTarget && (
        <NoteDialog
          open={dialog === 'note'}
          onClose={() => setDialog(null)}
          playerId={noteTarget.id}
          subject={noteTarget.name}
          onDone={() => setWriteCount((n) => n + 1)}
        />
      )}

      {brandId && (
        <div className="notice notice-quiet">
          <Icon name="info" />
          <div>
            Scoped to <strong>{brandLabel}</strong>. The table, the chip counts and the total in the
            header all carry this scope, so they agree with one another. The brand scope is global
            and persists across modules — clear it in the header.
          </div>
        </div>
      )}
    </>
  );
}
